import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { FiDownload } from "react-icons/fi";
import authHeader from "../Services/auth-header";
import axios from "axios";
import { refreshToken } from "../Services/auth.service";

function retryAxiosPost(url, formData, config, onSuccess, onFail, retries = 1, delay = 3000) {
  const { signal } = config;
  if (signal?.aborted) {
    onFail(new Error("Upload canceled"));
    return;
  }
  axios
    .post(url, formData, config)
    .then(onSuccess)
    .catch((err) => {
      if (signal?.aborted) {
        onFail(new Error("Upload canceled"));
        return;
      }
      if (err.response && err.response.status === 401) {
        refreshToken().then((response2) => {
          if (response2 !== false) {
            retryAxiosPost(url, formData, config, onSuccess, onFail, retries, delay);
          } else {
            onFail(err);
          }
        });
      } else if (retries > 0) {
        setTimeout(() => {
          retryAxiosPost(url, formData, config, onSuccess, onFail, retries - 1, delay);
        }, delay);
      } else {
        onFail(err);
      }
    });
}

const MultipleFileUpload = forwardRef(({
  baseURLUploadPdf,
  baseURLUploadImg,
  addUploadFileUrl,
  maxSizeMB = 5,
  acceptedFormats = ["image/jpeg", "image/png", "image/gif", "application/pdf"],
  t = (key) => key,
  setUploadedImages,
  setUploadedPDFs,
  detailsShow,
  addFailedFileToParent,
  onFilesChange,
}, ref) => {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const abortControllers = useRef({});

  useImperativeHandle(ref, () => ({
    cancelUpload: (fileId) => {
      const controller = abortControllers.current[fileId];
      if (controller) {
        controller.abort();
        delete abortControllers.current[fileId];
      }
      // حذف از state داخلی
      setFiles(prev => {
        const newFiles = prev.filter(f => f.id !== fileId);
        // همچنین والد را مطلع کن (برای به‌روزرسانی uploadingFiles)
        if (onFilesChange) onFilesChange(newFiles);
        return newFiles;
      });
    
    }
  }));

  useEffect(() => {
    if (onFilesChange) {
      onFilesChange(files);
    }
  }, [files, onFilesChange]);
  const validateFile = (file) => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      return {
        isValid: false,
        errorMessage: t("BigFileSize") ,
        errorType: "size",
      };
    }
    if (!acceptedFormats.includes(file.type)) {
      return {
        isValid: false,
        errorMessage: t("FileFormat"),
        errorType: "format",
      };
    }
    return { isValid: true };
  };

  const uploadFile = (fileItem) => {
    const { id, file, name, size } = fileItem;
    const controller = new AbortController();
    abortControllers.current[id] = controller;
    setFiles((prev) =>
      prev.map((f) => (f.id === id ? { ...f, status: "uploading", errorMessage: null } : f))
    );
  
    const formData = new FormData();
    formData.append("File", file);
    formData.append("UserFileName", name);
  
    const config = {
      headers: {
        "content-type": "multipart/form-data",
        ...authHeader(),
      },
      signal: controller.signal,
    };
  
    const isPDF = file.type === "application/pdf";
    const url = isPDF ? baseURLUploadPdf : baseURLUploadImg;
  
    const onSuccess = (response) => {
      delete abortControllers.current[id];
      // حذف فایل از state files (آپلود موفق)
      setFiles((prev) => prev.filter((f) => f.id !== id));
      if (addUploadFileUrl) {
        addUploadFileUrl(response.data, name, isPDF ? 1 : 2, file);
      }
    };
  
    const onFail = (err) => {
      delete abortControllers.current[id];
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
      const errorMsg = err?.response?.data?.message || "Upload failed. Please try again.";
      
      // حذف فایل از state files (آپلود ناموفق)
      setFiles((prev) => prev.filter((f) => f.id !== id));
      
      // اضافه کردن فایل خطادار به state والد
      if (addFailedFileToParent) {
        addFailedFileToParent(fileItem, errorMsg, "server");
      }
    };
  
    retryAxiosPost(url, formData, config, onSuccess, onFail, 1, 3000);
  };

  // داخل MultipleFileUpload
  const addFiles = (selectedFiles) => {
    const newFiles = [];
    Array.from(selectedFiles).forEach((file) => {
      const validation = validateFile(file);
      const fileId = `${Date.now()}-${Math.random()}-${file.name}`;
      
      if (validation.isValid) {
        // فایل معتبر: به files اضافه می‌شود (برای نمایش در حال آپلود)
        newFiles.push({
          id: fileId,
          file: file,
          name: file.name,
          size: file.size,
          status: "pending",
          errorMessage: null,
          errorType: null,
          uploadedSize: null,
          previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
          uploadedAt: new Date(),
        });
      } else {
        // فایل نامعتبر (حجم/فرمت): مستقیماً به state والد اضافه می‌شود
        const failedFileObj = {
          id: fileId,
          name: file.name,
          size: file.size,
          type: file.type,
          status: "error",
          errorMessage: validation.errorMessage,
          errorType: validation.errorType,
          url: null,
          uploadedAt: new Date(),
        };
        if (file.type === "application/pdf") {
          setUploadedPDFs((prev) => [...prev, failedFileObj]);
        } else {
          setUploadedImages((prev) => [...prev, failedFileObj]);
        }
      }
    });
  
    if (newFiles.length) {
      setFiles((prev) => [...prev, ...newFiles]);
      // شروع آپلود برای فایل‌های معتبر
      newFiles.forEach((fileItem) => {
        uploadFile(fileItem);
      });
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length) {
      addFiles(e.target.files);
      e.target.value = null;
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      addFiles(e.dataTransfer.files);
    }
  };

  return (
    <>
      <div
        className="project-details"
        style={{
          display: detailsShow ? "block" : "none",
          position: "absolute",
          bottom: "-1rem",
          left: "35%",
          transform: "translateX(-35%)",
          width: "100%",
        }}
      >
        <div className="multiple-file-uploadUploadFileNew">
          <div
            className="upload-dropzoneUploadFileNew"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileSelect}
              accept={acceptedFormats.join(",")}
              multiple
            />
            <div className="dropzone-contentUploadFileNew">
              <FiDownload size={28} />
              <span className="fw-bolder">{t("Choose a file or drop it here")}</span>
            </div>
          </div>
        </div>
      </div>
    </>
   );
});

export default MultipleFileUpload;
